import { api, get } from "../api.ts";
import { rerender } from "../rerender.ts";
import { state, wsBase } from "../state.ts";
import type { Member, Members } from "../types.ts";
import { append, busy, confirmDialog, h, hasTrainingAccess, personasLabel, toast, when, withTrainingAccess } from "../ui.ts";

const wsPath = () => `/v1/workspaces/${encodeURIComponent(state.ws!.id)}`;

/** Asks before removing someone, and whether to delete their training data too. */
function removeDialog(m: Member): Promise<{ remove: boolean; deleteTraining: boolean }> {
  return new Promise((resolve) => {
    const box = h("input", { type: "checkbox", id: "wipe" }) as HTMLInputElement;
    const dlg = h("dialog", { class: "dialog" });
    const done = (remove: boolean) => {
      const deleteTraining = box.checked;
      dlg.close();
      dlg.remove();
      resolve({ remove, deleteTraining });
    };
    append(
      dlg,
      h("h2", null, `Remove ${m.name ?? m.email}?`),
      h("p", null, "They lose their seat and the company workspace. Their personal workspace is not touched. To keep their history and take the seat back later, suspend them instead."),
      h("label", { class: "check" }, box, "Also delete their training progress in this company"),
      h(
        "div",
        { class: "actions" },
        h("button", { class: "danger", type: "button", onclick: () => done(true) }, "Remove from company"),
        h("button", { type: "button", onclick: () => done(false) }, "Cancel"),
      ),
    );
    dlg.addEventListener("cancel", () => done(false));
    document.body.append(dlg);
    dlg.showModal();
  });
}

function memberRow(m: Member, ownerView: boolean) {
  const isOwner = m.role === "owner";
  const isMe = m.user_id === state.overview?.me.user_id;
  // Only the owner changes an admin; nobody changes the owner.
  const editable = !isOwner && (ownerView || m.role !== "admin" || isMe);
  const patch = (body: Record<string, unknown>, done: string) =>
    api("PATCH", `${wsPath()}/members/${encodeURIComponent(m.user_id)}`, body).then(() => {
      toast(done);
      rerender();
    });

  const role = h("select", { "aria-label": `Role for ${m.email}`, disabled: !editable || !ownerView }) as HTMLSelectElement;
  for (const [v, t] of [["member", "Member"], ["admin", "Admin"]] as const) role.append(h("option", { value: v }, t));
  if (isOwner) role.append(h("option", { value: "owner" }, "Owner"));
  role.value = m.role;
  role.addEventListener("change", () => void patch({ role: role.value }, "Role changed.").catch((e) => toast(String(e.message ?? e), "error")));

  const training = h("input", { type: "checkbox", "aria-label": `Kai for Teams training for ${m.email}`, disabled: !editable }) as HTMLInputElement;
  training.checked = hasTrainingAccess(m.personas);
  training.addEventListener("change", () =>
    void patch({ personas: withTrainingAccess(m.personas, training.checked) }, training.checked ? "Training access given." : "Training access removed.").catch((e) => {
      training.checked = !training.checked;
      toast(String(e.message ?? e), "error");
    }),
  );

  const cap = h("input", { type: "number", min: 1, step: 1, inputmode: "numeric", placeholder: "No cap", "aria-label": `Monthly credit cap for ${m.email}`, disabled: !editable, class: "cap" }) as HTMLInputElement;
  cap.value = m.monthly_credit_cap == null ? "" : String(m.monthly_credit_cap);
  cap.addEventListener("change", () => {
    const v = cap.value.trim() === "" ? null : Math.round(Number(cap.value));
    if (v !== null && (!Number.isFinite(v) || v < 1)) return toast("A credit cap is a whole number above 0, or blank for none.", "error");
    void patch({ monthly_credit_cap: v }, "Credit cap saved.").catch((e) => toast(String(e.message ?? e), "error"));
  });

  const actions = h("td", { class: "row-actions" });
  if (editable && !isMe) {
    const suspend = h("button", { type: "button", class: "small" }, m.suspended_at ? "Restore" : "Suspend") as HTMLButtonElement;
    suspend.addEventListener("click", () =>
      busy(suspend, async () => {
        if (!m.suspended_at && !(await confirmDialog(`Suspend ${m.name ?? m.email}?`, "Their history stays and their seat is freed. They can't use the company workspace until restored.", "Suspend access", true))) return;
        await api("POST", `${wsBase()}/members/${encodeURIComponent(m.user_id)}/${m.suspended_at ? "restore" : "suspend"}`);
        toast(m.suspended_at ? "Access restored." : "Access suspended.");
        state.overview = null;
        rerender();
      }),
    );
    const remove = h("button", { type: "button", class: "small danger" }, "Remove") as HTMLButtonElement;
    remove.addEventListener("click", () =>
      busy(remove, async () => {
        const r = await removeDialog(m);
        if (!r.remove) return;
        await api("DELETE", `${wsPath()}/members/${encodeURIComponent(m.user_id)}${r.deleteTraining ? "?delete_training=true" : ""}`);
        toast(`${m.email} removed.`);
        state.overview = null;
        rerender();
      }),
    );
    actions.append(suspend, remove);
  }

  return h(
    "tr",
    { class: m.suspended_at ? "suspended" : "" },
    h("td", null, h("div", { class: "who" }, m.name ?? m.email), h("div", { class: "muted small" }, m.email), m.suspended_at && h("span", { class: "flag warn" }, `Suspended ${when(m.suspended_at)}`)),
    h("td", null, role),
    h("td", null, h("label", { class: "toggle" }, training, h("span", null, training.checked ? "On" : "Off")), h("div", { class: "muted small" }, personasLabel(m.personas))),
    h("td", null, cap),
    actions,
  );
}

export async function renderPeople(root: HTMLElement) {
  const m = await get<Members>(`${wsPath()}/members`);
  const ownerView = state.ws?.role === "owner";
  const free = m.seat_limit - m.seats_used;

  const email = h("input", { type: "email", required: true, autocomplete: "off", placeholder: "name@company.com", "aria-label": "Work email" }) as HTMLInputElement;
  const role = h("select", { "aria-label": "Role" }, h("option", { value: "member" }, "Member"), ownerView && h("option", { value: "admin" }, "Admin")) as HTMLSelectElement;
  const access = h("select", { "aria-label": "Buddies" }, h("option", { value: "training" }, "Kai for Teams only"), h("option", { value: "all" }, "All buddies")) as HTMLSelectElement;
  const send = h("button", { type: "submit", class: "primary", disabled: free <= 0 }, "Send invite") as HTMLButtonElement;
  const form = h("form", { class: "invite" }, email, role, access, send);
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    void busy(send, async () => {
      const r = await api<{ member?: unknown; invite?: unknown }>("POST", `${wsPath()}/invites`, {
        email: email.value.trim(),
        role: role.value,
        personas: access.value === "training" ? ["kai_teams"] : [],
      });
      toast(r.member ? `${email.value.trim()} already had an account and now has a seat.` : `Invite sent to ${email.value.trim()}. It lasts 14 days.`);
      rerender();
    });
  });

  const body = h("tbody");
  for (const x of m.members) body.append(memberRow(x, ownerView));

  append(
    root,
    h(
      "header",
      { class: "page-head" },
      h("div", null, h("h1", null, "People and access"), h("p", { class: "muted" }, `${m.seats_used} of ${m.seat_limit} seats used. Suspended people don't take a seat.`)),
    ),
    h(
      "section",
      null,
      h("h2", null, "Invite someone"),
      form,
      free <= 0 && h("p", { class: "late small" }, "Every seat is taken. Suspend or remove someone, cancel an invite, or add seats to your plan."),
    ),
    h(
      "div",
      { class: "table-wrap" },
      h(
        "table",
        { class: "members" },
        h("thead", null, h("tr", null, h("th", null, "Person"), h("th", null, "Role"), h("th", null, "Training access"), h("th", null, "Monthly credit cap"), h("th", null, h("span", { class: "sr" }, "Actions")))),
        body,
      ),
    ),
  );

  if (m.invites.length) {
    const ul = h("ul", { class: "plain" });
    for (const i of m.invites) {
      const cancel = h("button", { type: "button", class: "small" }, "Cancel invite") as HTMLButtonElement;
      cancel.addEventListener("click", () =>
        busy(cancel, async () => {
          await api("DELETE", `${wsPath()}/invites/${encodeURIComponent(i.id)}`);
          toast(`Invite to ${i.email} cancelled.`);
          rerender();
        }),
      );
      ul.append(h("li", { class: "invite-row" }, h("span", null, `${i.email}, ${i.role}, ${personasLabel(i.personas)}. Expires ${when(i.expires_at)}.`), cancel));
    }
    root.append(h("section", null, h("h2", null, "Waiting to join"), ul));
  }
}
